/**
 * AlertRecipientsTab
 *
 * UI inside AccountDetailPage that lets:
 *   • Account owners manage the global recipient list (rows where
 *     source_user_id IS NULL). The owner is auto-seeded by a trigger.
 *   • Members add/remove their OWN additional recipients (rows where
 *     source_user_id = auth.uid()), which only fire when that member is
 *     the alert sender.
 *
 * Everyone in the account can SEE the full list (read-only), but RLS prevents
 * them from editing rows they don't own.
 *
 * The toggle (enabled/disabled) lets the owner opt out of receiving alerts
 * without losing their seat.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Trash2, Bell, BellOff, Crown, User } from 'lucide-react';
import {
  fetchAlertRecipients,
  addAlertRecipient,
  removeAlertRecipient,
  setAlertRecipientEnabled,
  type AlertRecipient,
} from '@/services/accounts';
import { useAuthStore } from '@/stores/auth-store';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { AccountMemberWithProfile } from '@asi/shared';

interface Props {
  accountId: string;
  members: AccountMemberWithProfile[];
}

export function AlertRecipientsTab({ accountId, members }: Props) {
  const currentUserId = useAuthStore((s) => s.profile?.id ?? null);
  const currentUserRole = useAuthStore((s) => s.profile?.role ?? null);
  const [recipients, setRecipients] = useState<AlertRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [adding, setAdding] = useState<'global' | 'mine' | null>(null);
  const [pickUserId, setPickUserId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isOwner = useMemo(
    () => members.some((m) => m.user_id === currentUserId && m.role === 'owner'),
    [members, currentUserId],
  );
  // Staff can manage global recipients on behalf of any account
  const isStaff = ['operator', 'admin', 'super_admin'].includes(currentUserRole ?? '');
  const canManageGlobals = isOwner || isStaff;

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchAlertRecipients(accountId);
      setRecipients(rows);
    } catch (e) {
      setError((e as { message?: string })?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // Split into global vs per-member-mine
  const globals = recipients.filter((r) => r.source_user_id === null);
  const mine = recipients.filter((r) => r.source_user_id === currentUserId);

  // Candidate users to add: account members not already on the relevant list.
  const globalCandidates = members.filter(
    (m) => !globals.some((r) => r.user_id === m.user_id),
  );
  const mineCandidates = members.filter(
    (m) =>
      m.user_id !== currentUserId &&
      !mine.some((r) => r.user_id === m.user_id) &&
      !globals.some((r) => r.user_id === m.user_id),
  );

  async function handleToggle(r: AlertRecipient) {
    setBusyId(r.id);
    try {
      await setAlertRecipientEnabled(r.id, !r.enabled);
      setRecipients((prev) =>
        prev.map((x) => (x.id === r.id ? { ...x, enabled: !r.enabled } : x)),
      );
    } catch (e) {
      setError((e as { message?: string })?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function handleRemove(r: AlertRecipient) {
    if (!confirm(`Quitar a ${r.profile?.full_name ?? 'este usuario'} de los receptores?`)) return;
    setBusyId(r.id);
    try {
      await removeAlertRecipient(r.id);
      setRecipients((prev) => prev.filter((x) => x.id !== r.id));
    } catch (e) {
      setError((e as { message?: string })?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function handleAdd(scope: 'global' | 'mine') {
    if (!pickUserId) return;
    setBusyId('adding');
    try {
      await addAlertRecipient(
        accountId,
        pickUserId,
        scope === 'global' ? null : currentUserId,
      );
      setPickUserId('');
      setAdding(null);
      await load();
    } catch (e) {
      setError((e as { message?: string })?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Cargando receptores...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="px-3 py-2 rounded-lg bg-destructive/10 text-destructive text-sm">
          {error}
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        Cuando un miembro de esta cuenta envía una alerta (SOS, pánico, incendio o emergencia
        médica), todos los usuarios habilitados en esta lista reciben una notificación push.
        El emisor nunca se notifica a sí mismo.
      </p>

      {/* ── Sección 1: Receptores globales ─────────────────────────────── */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Receptores predeterminados
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Reciben TODAS las alertas de la cuenta. Solo el propietario puede modificarlos.
            </p>
          </div>
          {canManageGlobals && globalCandidates.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => setAdding('global')}>
              <Plus className="w-4 h-4 mr-1" /> Agregar
            </Button>
          )}
        </div>

        {globals.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-center text-muted-foreground text-sm">
              Sin receptores predeterminados.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {globals.map((r) => (
              <RecipientRow
                key={r.id}
                recipient={r}
                canEdit={canManageGlobals}
                busy={busyId === r.id}
                onToggle={() => handleToggle(r)}
                onRemove={() => handleRemove(r)}
                isOwnerSeat={members.some(
                  (m) => m.user_id === r.user_id && m.role === 'owner',
                )}
              />
            ))}
          </div>
        )}

        {adding === 'global' && (
          <AddRow
            users={globalCandidates}
            value={pickUserId}
            onChange={setPickUserId}
            onCancel={() => {
              setAdding(null);
              setPickUserId('');
            }}
            onConfirm={() => handleAdd('global')}
            saving={busyId === 'adding'}
          />
        )}
      </section>

      {/* ── Sección 2: Mis receptores adicionales ───────────────────────── */}
      {currentUserId && (
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-foreground flex items-center gap-2">
                <User className="w-4 h-4" />
                Mis receptores adicionales
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Solo se notifican cuando <strong>tú</strong> envías la alerta. Se suman a los
                predeterminados.
              </p>
            </div>
            {mineCandidates.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => setAdding('mine')}>
                <Plus className="w-4 h-4 mr-1" /> Agregar
              </Button>
            )}
          </div>

          {mine.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center text-muted-foreground text-sm">
                Sin receptores adicionales.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {mine.map((r) => (
                <RecipientRow
                  key={r.id}
                  recipient={r}
                  canEdit={true}
                  busy={busyId === r.id}
                  onToggle={() => handleToggle(r)}
                  onRemove={() => handleRemove(r)}
                />
              ))}
            </div>
          )}

          {adding === 'mine' && (
            <AddRow
              users={mineCandidates}
              value={pickUserId}
              onChange={setPickUserId}
              onCancel={() => {
                setAdding(null);
                setPickUserId('');
              }}
              onConfirm={() => handleAdd('mine')}
              saving={busyId === 'adding'}
            />
          )}
        </section>
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────

interface RecipientRowProps {
  recipient: AlertRecipient;
  canEdit: boolean;
  busy: boolean;
  onToggle: () => void;
  onRemove: () => void;
  isOwnerSeat?: boolean;
}

function RecipientRow({ recipient, canEdit, busy, onToggle, onRemove, isOwnerSeat }: RecipientRowProps) {
  const name = recipient.profile?.full_name ?? 'Usuario';
  const email = recipient.profile?.email ?? '';
  const initial = name.charAt(0).toUpperCase();
  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3 rounded-xl border bg-card',
        recipient.enabled ? 'border-border' : 'border-border/50 opacity-60',
      )}
    >
      <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-sm font-medium text-muted-foreground shrink-0">
        {initial}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-foreground truncate">{name}</p>
          {isOwnerSeat && (
            <Badge className="text-xs flex items-center gap-1 bg-amber-500/10 text-amber-600">
              <Crown className="w-3 h-3" />
              Propietario
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate">{email}</p>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        {canEdit ? (
          <>
            <div className="flex items-center gap-1.5">
              {recipient.enabled ? (
                <Bell className="w-3.5 h-3.5 text-foreground" />
              ) : (
                <BellOff className="w-3.5 h-3.5 text-muted-foreground" />
              )}
              <Switch
                checked={recipient.enabled}
                disabled={busy}
                onCheckedChange={onToggle}
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              disabled={busy}
              onClick={onRemove}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <Badge variant="secondary" className="text-xs">
            {recipient.enabled ? 'Activo' : 'Pausado'}
          </Badge>
        )}
      </div>
    </div>
  );
}

interface AddRowProps {
  users: AccountMemberWithProfile[];
  value: string;
  onChange: (v: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  saving: boolean;
}

function AddRow({ users, value, onChange, onCancel, onConfirm, saving }: AddRowProps) {
  return (
    <div className="flex items-center gap-2 p-3 rounded-xl border border-dashed border-border bg-muted/30">
      <select
        className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Seleccionar miembro...</option>
        {users.map((m) => (
          <option key={m.id} value={m.user_id}>
            {m.profile?.full_name ?? 'Usuario'} ({m.profile?.email ?? '—'})
          </option>
        ))}
      </select>
      <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
        Cancelar
      </Button>
      <Button size="sm" onClick={onConfirm} disabled={!value || saving}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Agregar'}
      </Button>
    </div>
  );
}
